import Button from '@/shared/Button'
import FormAutocomplete from '@/shared/FormCreator/FormItem/FormAutocomplete'

import Chip from '@mui/material/Chip'
import Popper from '@mui/material/Popper'
import Stack from '@mui/material/Stack'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'

import Box from '@mui/material/Box'

import HighlightOff from '@mui/icons-material/HighlightOff'
import IconButton from '@mui/material/IconButton'

import { useEffect, useRef, useState } from 'react'

import debounce from '@mui/material/utils/debounce'

import ConfirmDialog from '@/shared/ConfirmDialog'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { alpha } from '@mui/material/styles'

const mapStateToProps = ({ loading }) => ({
  isLoading:
    loading['courseTemplate/getCourseTemplateById'] ||
    loading['courseTemplate/editCourseTemplateLabelVideosById'],
  isCoursesLoading: loading['courseTemplate/getCourseTemplatesAddable'],
})

const Selection = ({ data, onRemove }) => (
  <Chip
    icon={
      <IconButton
        size="small"
        sx={{ padding: 0 }}
        onClick={() => {
          onRemove(data?.id)
        }}
      >
        <HighlightOff />
      </IconButton>
    }
    label={data?.label}
  />
)

const EditCategoryVideoDialog = ({
  dispatch,
  isLoading,
  isCoursesLoading,
  onClose,
  onComplete,
  categoryData,
  ...props
}) => {
  const { t } = useTranslation()

  const searchInputRef = useRef()

  const [page, setPage] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selected, setSelected] = useState([])
  const [selectedSearch, setSelectedSearch] = useState()

  const { data: searchResultsData = [], paging } = searchResults

  const addDisabled = selected.find(
    res => res.label.toLowerCase() === selectedSearch?.toLowerCase(),
  )

  const handleClose = () => {
    setSelected([])
    setSearchInput('')
    setSearchResults([])
    searchInputRef.current = ''
    onClose?.()
  }

  const handleSubmit = () => {
    const courseIdList = selected.map(s => s.id)

    dispatch({
      type: 'courseTemplate/editCourseTemplateLabelVideosById',
      payload: {
        data: { courseIdList },
        labelId: categoryData.labelId,
      },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: t('messages.video_created'), severity: 'success' },
        })
        onComplete?.()
        handleClose?.()
      },
    })
  }

  const handleSelectionRemove = id => {
    setSelected(selected.filter(s => s.id !== id))
  }

  const handleAddCategory = () => {
    const existing = searchResultsData.find(
      res => res.label.toLowerCase() === selectedSearch.toLowerCase(),
    )

    if (existing) {
      setSelected([existing, ...selected])
    }
  }

  const setCategorySearchDebounce = debounce(value => {
    setSearchInput(value)
  }, 400)

  const loadMoreDebounce = debounce(() => {
    setPage(p => p + 1)
  }, 400)

  useEffect(() => {
    dispatch({
      type: 'courseTemplate/getCourseTemplatesAddable',
      payload: { searchCourseName: searchInput, pageIndex: page, pageSize: 10 },
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.templateId, label: d.name }))

        setSearchResults(s => ({
          data: pageIndex > 0 ? [...(s.data || []), ...newData] : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, searchInput, page])

  useEffect(() => {
    if (categoryData) {
      const { courseTempList } = categoryData
      const transformedOptions = courseTempList.map(({ templateId, name }) => ({
        label: name,
        id: templateId,
      }))

      if (transformedOptions) {
        setSelected(transformedOptions)
      }
    }
  }, [categoryData])

  useEffect(() => {
    if (searchResultsData?.length === 1) {
      setSelectedSearch(searchResultsData[0].label)
    }
  }, [searchResultsData])

  return (
    <ConfirmDialog
      {...props}
      title={t('general.add_video')}
      onClose={handleClose}
      onSubmit={() => {
        handleSubmit()
      }}
      loading={isLoading}
      contentProps={{ sx: { width: 500 } }}
    >
      <Typography fontWeight="bold" mb={2}>{`${t('general.category_name')}: ${
        categoryData?.name
      }`}</Typography>
      <Stack mb={10} direction="row" justifyContent="flex-start">
        <Box>
          <FormAutocomplete
            inputConfig={{
              onInputChange: (_, value, reason) => {
                searchInputRef.current = value

                if (['input', 'clear'].includes(reason)) {
                  setPage(0)
                  setCategorySearchDebounce(value)
                  setSelectedSearch()
                }

                if (reason === 'reset') {
                  setSelectedSearch(value)
                }
              },
              sx: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                '& .Mui-focused': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${alpha('#000', 0.25)} !important`,
                  borderWidth: '1px !important',
                  boxShadow: 'none !important',
                },
              },
              ListboxProps: {
                role: 'list-box',
                sx: { maxHeight: 200 },
                onScroll: event => {
                  const listboxNode = event.currentTarget

                  if (
                    listboxNode.scrollTop + listboxNode.clientHeight >=
                    listboxNode.scrollHeight - 10
                  ) {
                    const { hasNext } = paging

                    if (!isCoursesLoading && hasNext) {
                      loadMoreDebounce()
                    }
                  }
                },
              },
            }}
            options={searchResultsData}
            getOptionDisabled={option => selected.some(s => s.id === option.id)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            PopperComponent={popperProps => (
              <Popper
                elevation={0}
                {...popperProps}
                sx={{
                  '& .MuiPaper-root': {
                    borderTopRightRadius: 0,
                    borderTopLeftRadius: 0,
                    boxShadow: 0,
                    border: `1px solid ${alpha('#000', 0.25)}`,
                  },
                }}
              />
            )}
          />
        </Box>
        <Button
          sx={{ ml: 2 }}
          variant="outlined"
          color="primary"
          disabled={!selectedSearch || !!addDisabled}
          onClick={() => {
            handleAddCategory()
          }}
        >
          {t('general.add')}
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 5 }}>
        <CardContent sx={{ minHeight: 100 }}>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            {selected.map(obj => (
              <Selection key={obj?.id} data={obj} onRemove={handleSelectionRemove} />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </ConfirmDialog>
  )
}

export default connect(mapStateToProps)(EditCategoryVideoDialog)
